/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  ChevronDown,
  Mail,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  Send,
  Inbox,
  Sparkles,
  Info,
} from "lucide-react";
import "../../agentic.module.css";
import "./emailTriaging.css";
import styles from "../../../Samarth Module/pages/Alerts/alert.module.css";
import { useRegisterAiChatScreen } from "../../utils/aiChatScreenContext";
import {
  ListItem,
  Box,
  Typography,
  Snackbar,
  Drawer,
  CircularProgress,
} from "@mui/material";
import Excel from "../../../assets/images/Excel.svg";
import Filters from "../../../assets/images/filters.svg";
import Slider from "../../../assets/images/sliders 1.svg";
import DraftEmail from "./draftEmail";
import ColumnSortIcon from "Agentic Module/components/common/columnSortIcon";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import {
  MonitorTodayPageSkeleton,
  TableBodySkeletonRows,
  PaginationSkeleton,
} from "../../components/skeletonLoader/AgenticPageSkeleton";
import { useMsal } from "@azure/msal-react";
import { useLocation } from "react-router-dom";
import MoreFilters from "./MoreFilters";
import { emailTriageGetKpis, emailTriageGetFilters, emailTriageGetDispatches, emailTriageGetDispatchesV2, emailTriageGetEmailHistory, emailTriageGetUserPrefs, emailTriageSaveUserPrefs, emailTriageExportData, extractDispatchMailCounts, normalizeDispatchRow } from "./emailTriageApi";

import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import resetBtn from "../../../assets/images/resetBtn.png";

import {
  USER_PREFS_SCREEN_ID,
  EMAIL_TRIAGE_SELECTED_FILTERS_STORAGE_KEY,
  DISPATCH_COLUMN_MAP,
  DISPATCH_COLUMNS,
  STICKY_CLASS_MAP,
} from "./emailTriageConstants";

import {
  generateLabel,
  buildDispatchColumnPreferenceList,
  mapUserPrefsToColumnList,
  buildUserPrefsPayload,
  normalizePrefIsActive,
  resolvePrefFilterNameToKey,
  getEmailTriageDispatchRowKey,
  mapDispatchRowToRmpmAlertDetails,
  buildRmpmAlertLookupPayload,
  getUserSiteScopeFromRmpmAlertStorage,
  applyUserScopeToFilters,
  hasUserScope,
  normalizeUserScope,
  buildInitialSelectedFilters,
  loadPersistedSelectedFilters,
  savePersistedSelectedFilters,
  clearPersistedSelectedFilters,
  formatEmailTriageFilterOptionsForDisplay,
  toDisplayEmailTriageFilterValues,
  toApiEmailTriageFilterValues,
  formatDispatchStatusFilterLabel,
  normalizeDispatchStatusFilterApiValues,
} from "./emailTriageUtils";
import AlertInsight from "Samarth Module/pages/Alerts/insight/alertInsight";
import { alertDetailAPI } from "Samarth Module/api/api";
import MultiSelectWithSearchInput from "../../components/infiniteMultiSelect/withInfiniteMultiSelect";
import TripTrackingModal from "../tntLinkage/tripTrackingModal";
import PropTypes from "prop-types";

const DISPATCH_CELL_TITLE_HANDLERS = {
  srNo: (row) => stringifyOrEmpty(row.srNo),
  poNumber: (row) => stringifyOrEmpty(row.poNumber),
  unitCode: (row) => stringifyOrEmpty(row.unitCode),
  vendorName: (row) => stringifyOrEmpty(row.vendorName),
  supplierInventory: (row) => stringifyOrEmpty(row.supplierInventory),
  materialCode: (row) => stringifyOrEmpty(row.materialCode),
  materialDescription: (row) => stringifyOrEmpty(row.materialDescription),
  rca: (row) => stringifyOrEmpty(row.rca),
  shortageDate: (row) => stringifyOrEmpty(row.shortageDate),
  alertId: (row) => stringifyOrEmpty(row.alertId),
  alertWeek: (row) => stringifyOrEmpty(row.alertWeek),

  originalDispatchDate: (row) => formatDisplayDdMmYy(row.originalDispatchDate) || "",
  originalDeliveryDate: (row) => formatDisplayDdMmYy(row.originalDeliveryDate) || "",
  updatedDispatchDate: (row) => formatDisplayDdMmYy(row.updatedDispatchDate) || "",
  updatedDeliveryDate: (row) => formatDisplayDdMmYy(row.updatedDeliveryDate) || "",
  eta: (row) => formatDisplayDdMmYy(row.eta) || "",
  updatedEta: (row) => formatDisplayDdMmYy(row.updatedEta) || "",

  qtyPlanned: getQtyPlannedTitle,
  qtyAvailable: getQtyAvailableTitle,
  criticalityIndex: getCriticalityIndexTitle,
  dispatchStatus: (row) => getDispatchStatusDisplayLabel(row.dispatchStatus),
  noOfDayDelayed: getNoOfDayDelayedTitle,
  trip_id: getTripIdTitle,
  last_communication: getLastCommunicationTitle,
  mailRefs: getLastCommunicationTitle,
  _action: () => "",
};

const DISPATCH_COLUMN_TOOLTIPS = {
  source_identifier: "Shows the source currently providing the latest dispatch and delivery information for this PO.",
  dispatchStatus: "Indicates whether the material is expected on time, delayed, or early, and whether it has been dispatched.",
  trigger: "Explains why this PO may require supplier or logistics follow-up.",
  triggers: "Explains why this PO may require supplier or logistics follow-up.",
  rca: "Indicates who is responsible for the delay, if any.",
  noOfDayDelayed: "Number of days the expected arrival differs from the original delivery commitment.",
  eta: "Latest expected arrival date based on the most recent available information.",
  updatedDispatchDate: "Latest known or expected date when the supplier will dispatch the material.",
  updatedDeliveryDate: "Planned arrival date based on dispatch information and transit lead time.",
  updatedEta: "Refreshed arrival date after considering the latest shipment updates.",
  isActive: "Indicates whether this PO line is still open and requires monitoring.",
  active: "Indicates whether this PO line is still open and requires monitoring.",
  linkedRddWeek: "Delivery commitment week used for planning and shortage tracking.",
  linked_rdd_week: "Delivery commitment week used for planning and shortage tracking.",
  linkedStaWeek: "Expected arrival week used for supply visibility and planning.",
  linked_sta_week: "Expected arrival week used for supply visibility and planning.",
  alertStatus: "Current status of the associated supply risk or shortage alert.",
  alert_status: "Current status of the associated supply risk or shortage alert.",
};

const ROOT_CAUSE_VALUE_TOOLTIPS = {
  "supplier delay": "Delay is primarily caused by late dispatch from the supplier.",
  "transporter delay": "Material was dispatched on time, but transportation delays are impacting delivery.",
  "supplier delay / transporter delay": "Both supplier dispatch delays and transportation delays are contributing to the late delivery.",
  "no delay": "No supplier or transportation delay has been identified.",
};

function getRootCauseValueTooltip(rootCause) {
  const normalized = String(rootCause ?? "").trim().toLowerCase();
  return ROOT_CAUSE_VALUE_TOOLTIPS[normalized] || "";
}

function parseEtaDate(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const ddmmyyyySlash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ddmmyyyySlash) {
    const [, dd, mm, yyyy] = ddmmyyyySlash;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ddmmyyyyDash = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddmmyyyyDash) {
    const [, dd, mm, yyyy] = ddmmyyyyDash;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const [, yyyy, mm, dd] = ymd;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? null : t;
}

export function formatDisplayDdMmYy(raw) {
  const date = parseEtaDate(raw);
  if (!date) return raw == null || raw === "" ? "" : String(raw);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** CSS class for `.status.*` badges — shared by Dispatch Status and ETA columns */
function getDispatchStatusBadgeClass(dispatchStatus) {
  const raw = String(dispatchStatus ?? "").trim().toLowerCase();
  if (!raw) return "on_time";
  const normalized = raw.replace(/[\s-]+/g, "_");
  if (normalized.includes("delayed") || raw.includes("delayed")) return "delayed";
  if (normalized.includes("preponed") || raw.includes("preponed")) return "preponed";
  if (
    normalized.includes("on_time") ||
    raw.includes("on time") ||
    normalized === "open" ||
    raw === "open"
  ) {
    return "on_time";
  }
  return "on_time";
}

function getDispatchStatusDisplayLabel(dispatchStatus) {
  return formatDispatchStatusFilterLabel(dispatchStatus) || "-";
}

function getDispatchStatusValueTooltip(dispatchStatus) {
  const normalized = String(dispatchStatus ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  let timing = "on the original commitment";

  if (normalized.includes("preponed")) {
    timing = "earlier than the original commitment";
  } else if (normalized.includes("delayed")) {
    timing = "later than the original commitment";
  }
  let movement = "The material's dispatch state is being monitored.";

  if (normalized.includes("in_transit")) {
    movement =
      "The material has been dispatched and is currently in transit.";
  } else if (normalized.includes("not_dispatched")) {
    movement = "The material has not yet been dispatched.";
  }

  return `Expected ${timing}. ${movement}`;
}

function isDispatchOnTimeStatus(dispatchStatus) {
  return getDispatchStatusBadgeClass(dispatchStatus) === "on_time";
}

const KPI_DISPATCH_STATUS_FILTERS = {
  all: [],
  preponed: ["Preponed - Not Dispatched", "Preponed - In Transit"],
  on_time: ["On-Time - Not Dispatched", "On-Time - In Transit"],
  delayed: ["Delayed - Not Dispatched", "Delayed - In Transit"],
};

function hasKpiDispatchStatusFilter(selectedValues, kpiValues) {
  const selected = Array.isArray(selectedValues) ? selectedValues : [];
  if (selected.length !== kpiValues.length) return false;
  const selectedSet = new Set(selected.map((value) => String(value)));
  return kpiValues.every((value) => selectedSet.has(value));
}

const TOTAL_KPI_BREAKDOWN_MODES = {
  Preponed: "preponed",
  "On-Time": "on_time",
  Delayed: "delayed",
};

function isDispatchStatusLabelSelected(selectedValues, label) {
  const selected = Array.isArray(selectedValues) ? selectedValues : [];
  if (selected.length === 0) return false;
  const labelValue = String(label);
  const apiValue = String(toApiEmailTriageFilterValues("dispatch_status", [label])[0]);

  return selected.some((value) => {
    const str = String(value);
    if (str === labelValue || str === apiValue) return true;
    return formatDispatchStatusFilterLabel(value) === labelValue;
  });
}

function isCategoryKpiBreakdownActive(selectedValues, label, categoryMode) {
  if (isDispatchStatusLabelSelected(selectedValues, label)) return true;
  return hasKpiDispatchStatusFilter(
    selectedValues,
    KPI_DISPATCH_STATUS_FILTERS[categoryMode] || [],
  );
}

function isKpiCardFilterActive(selectedValues, mode) {
  if (mode === "all") return !selectedValues?.length;
  const categoryValues = KPI_DISPATCH_STATUS_FILTERS[mode] || [];
  if (hasKpiDispatchStatusFilter(selectedValues, categoryValues)) return true;
  return categoryValues.some((label) => isDispatchStatusLabelSelected(selectedValues, label));
}

function isTotalKpiBreakdownActive(selectedValues, mode) {
  const categoryValues = KPI_DISPATCH_STATUS_FILTERS[mode] || [];
  if (hasKpiDispatchStatusFilter(selectedValues, categoryValues)) return true;
  return categoryValues.some((label) => isDispatchStatusLabelSelected(selectedValues, label));
}

function renderKpiMainMetric(metric, { isActive, onClick }) {
  return (
    <button
      type="button"
      className={`summary-kpi-total summary-metric summary-metric--main summary-metric--button${isActive ? " summary-metric--active" : ""
        }`}
      onClick={onClick}
      aria-pressed={isActive}
    >
      <div className="summary-label">{metric.label}</div>
      <div className="summary-value">{metric.value}</div>
    </button>
  );
}

function renderKpiBreakdownMetric(metric, { isActive, onClick }) {
  return (
    <button
      type="button"
      key={metric.key}
      className={`summary-metric summary-metric--sub summary-metric--button${isActive ? " summary-metric--active" : ""
        }`}
      onClick={() => onClick(metric)}
      aria-pressed={isActive}
    >
      <div className="summary-label">{metric.label}</div>
      <div className="summary-value">{metric.value}</div>
    </button>
  );
}

function renderKpiMetricGroup(kpis, mainLabel, categoryKey, { mainConfig, breakdownConfig } = {}) {
  const kpi = Array.isArray(kpis)
    ? kpis.find((k) => k.catagory === categoryKey)
    : undefined;
  const subs = Array.isArray(kpi?.sub_catagory) ? kpi.sub_catagory : [];
  const mainMetric = {
    key: `${categoryKey}-main`,
    label: mainLabel,
    value: kpi?.count ?? 0,
  };
  const breakdownMetrics = subs.map((sub, idx) => ({
    key: `${categoryKey}-sub-${sub.catagory ?? idx}`,
    label: sub.catagory,
    value: sub.count ?? 0,
  }));

  return (
    <>
      {renderKpiMainMetric(mainMetric, {
        isActive: mainConfig?.isActive ?? false,
        onClick: mainConfig?.onClick ?? (() => { }),
      })}
      {breakdownMetrics.length > 0 ? (
        <div className="summary-kpi-breakdown" data-metric-count={breakdownMetrics.length}>
          {breakdownMetrics.map((metric) =>
            renderKpiBreakdownMetric(metric, {
              isActive: breakdownConfig?.isBreakdownActive?.(metric.label) ?? false,
              onClick: breakdownConfig?.onBreakdownClick ?? (() => { }),
            }),
          )}
        </div>
      ) : null}
    </>
  );
}

function renderKpiTotalMetric(label, value, breakdownMetrics = [], { mainConfig, breakdownConfig } = {}) {
  const breakdown = Array.isArray(breakdownMetrics) ? breakdownMetrics : [];
  const mainMetric = { key: "total-main", label, value };

  return (
    <>
      {renderKpiMainMetric(mainMetric, {
        isActive: mainConfig?.isActive ?? false,
        onClick: mainConfig?.onClick ?? (() => { }),
      })}
      {breakdown.length > 0 ? (
        <div className="summary-kpi-breakdown" data-metric-count={breakdown.length}>
          {breakdown.map((metric) =>
            renderKpiBreakdownMetric(metric, {
              isActive: breakdownConfig?.isBreakdownActive?.(metric.label) ?? false,
              onClick: breakdownConfig?.onBreakdownClick ?? (() => { }),
            }),
          )}
        </div>
      ) : null}
    </>
  );
}

function buildCommunicationMeta(sent, recv, status = "") {
  return {
    sent: Number.isFinite(sent) ? sent : 0,
    recv: Number.isFinite(recv) ? recv : 0,
    status,
  };
}

function resolveCommunicationStatus(row) {
  const explicitStatus =
    row.lastCommunicationStatus ??
    row.communicationStatus ??
    row.last_communication_summary;

  if (explicitStatus != null && String(explicitStatus).trim() !== "") {
    return String(explicitStatus).trim();
  }

  const dispatchStatus = String(row.dispatchStatus || "").toLowerCase();
  const targetDate =
    row.updatedDeliveryDate ||
    row.eta ||
    row.updatedDispatchDate;

  const formattedDate = targetDate
    ? formatDisplayDdMmYy(targetDate)
    : "";

  if (!formattedDate) {
    return "";
  }

  return dispatchStatus === "delayed"
    ? `Delayed to ${formattedDate}`
    : `ETA ${formattedDate}`;
}

/** Mail counts from API: replyMailCount (sent/replies), inboxMailCount (received). */
function getLastCommunicationMeta(row) {
  const mail = extractDispatchMailCounts(row);

  return buildCommunicationMeta(
    mail.replyMailCount,
    mail.inboxMailCount,
    resolveCommunicationStatus(row),
  );
}

/** Parse trip_id when API sends a JSON array string, array, or plain id. */
function parseTripIds(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseTripIds(item)).filter(Boolean);
  }
  const raw = String(value).trim();
  if (!raw) return [];
  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      return parseTripIds(JSON.parse(raw));
    } catch {
      // Fall through.
    }
  }
  if (raw.includes(",")) {
    return raw
      .split(",")
      .map((s) => s.trim().replace(/(?:^["']|["']$)/g, ""))
      .filter(Boolean);
  }
  return [raw.replace(/(?:^["']|["']$)/g, "")];
}

function stringifyOrEmpty(value) {
  return value != null ? String(value) : "";
}
function getQtyWithUomTitle(qty, uom) {
  if (qty == null || qty === "") {
    return "";
  }

  const uomSuffix = uom ? ` ${uom}` : "";

  return `${qty}${uomSuffix}`;
}

function getQtyPlannedTitle(row) {
  const qty = row.qtyPlanned?.toLocaleString?.() ?? row.qtyPlanned;
  return getQtyWithUomTitle(qty, row.uom);
}

function getQtyAvailableTitle(row) {
  if (row.qtyAvailable == null || row.qtyAvailable === "") return "";
  return getQtyWithUomTitle(Number(row.qtyAvailable).toLocaleString(), row.uom);
}

function getCriticalityIndexTitle(row) {
  if (row.criticalityIndex == null || row.criticalityIndex === "") return "";
  return `${Math.round(Number(row.criticalityIndex))}%`;
}

function getNoOfDayDelayedTitle(row) {
  return row.noOfDayDelayed != null ? `${row.noOfDayDelayed} Days` : "";
}

function getTripIdTitle(row) {
  const ids = parseTripIds(row.trip_id);
  return ids.length ? ids.join(",") : "";
}

function getLastCommunicationTitle(row) {
  const m = getLastCommunicationMeta(row);
  const parts = [];

  if (m.sent) parts.push(`Reply: ${m.sent}`);
  if (m.recv) parts.push(`Inbox: ${m.recv}`);
  if (m.status) parts.push(m.status);

  return parts.join(" | ");
}

function getDispatchCellTitle(row, columnKey) {
  const handler = DISPATCH_CELL_TITLE_HANDLERS[columnKey];

  return handler
    ? handler(row)
    : stringifyOrEmpty(row[columnKey]);
}

function renderLastCommunicationCell(row, columnKey) {
  const m = getLastCommunicationMeta(row);
  const cellTitle = getDispatchCellTitle(row, columnKey) || undefined;
  return (
    <td key={columnKey} className="email-triage-last-comm" title={cellTitle}>
      <div className="email-triage-last-comm-icons">
        <span
          className="email-triage-last-comm-pill"
          style={{ color: "#3b82f6" }}
          title="Reply mail count"
        >
          <Send size={12} aria-hidden /> <span style={{ color: "black" }}>{m.sent}</span>
        </span>
        <span style={{ color: "#d1d5db" }}>|</span>
        <span
          className="email-triage-last-comm-pill"
          style={{ color: "#f59e0b" }}
          title="Inbox mail count"
        >
          <Inbox size={12} aria-hidden /> <span style={{ color: "black" }}>{m.recv}</span>
        </span>
      </div>
      <div className="email-triage-last-comm-status">
        <span style={{ fontWeight: 600, color: "black" }}>
          Status : {"-"}
        </span>
      </div>
    </td>
  );
}

function resolveDispatchVendorName(row) {
  return String(
    row?.vendorName ??
    row?.vendor_name ??
    row?.supplierName ??
    row?.supplier_name ??
    row?.["Supplier name"] ??
    "",
  ).trim();
}

function groupRowsByVendor(rows) {
  const groups = [];
  const indexByVendor = new Map();
  for (const row of rows) {
    const v = resolveDispatchVendorName(row) || "—";
    let idx = indexByVendor.get(v);
    if (idx === undefined) {
      idx = groups.length;
      indexByVendor.set(v, idx);
      groups.push({ vendor: v, rows: [] });
    }
    groups[idx].rows.push(row);
  }
  return groups;
}

function getTotalKpiCount(kpis) {
  if (!Array.isArray(kpis)) return 0;
  return kpis.reduce((sum, kpi) => sum + (Number(kpi?.count) || 0), 0);
}

function getKpiCategoryCount(kpis, categoryKey) {
  if (!Array.isArray(kpis)) return 0;
  const kpi = kpis.find((item) => item?.catagory === categoryKey);
  return Number(kpi?.count) || 0;
}

function getTotalKpiBreakdown(kpis) {
  return [
    {
      key: "total-preponed",
      label: "Preponed",
      value: getKpiCategoryCount(kpis, "Preponed"),
    },
    {
      key: "total-on-time",
      label: "On-Time",
      value: getKpiCategoryCount(kpis, "On-Time"),
    },
    {
      key: "total-delayed",
      label: "Delayed",
      value: getKpiCategoryCount(kpis, "Delayed"),
    },
  ];
}
function getVendorGroupMeta(group) {
  const groupNoComm = group.rows.every((row) => {
    const meta = getLastCommunicationMeta(row);
    return meta.sent === 0 && meta.recv === 0 && !meta.status;
  });

  const groupActionRow =
    group.rows.find((row) => !isDispatchOnTimeStatus(row.dispatchStatus)) ||
    group.rows[0];

  const groupActionDisabled = group.rows.every((row) =>
    isDispatchOnTimeStatus(row.dispatchStatus),
  );

  return {
    groupNoComm,
    groupSize: group.rows.length,
    groupActionRow,
    groupActionDisabled,
    actionStickyClass: STICKY_CLASS_MAP["_action"] || "",
  };
}

function isNumericCell(value, columnKey, dispatchColumnMapExtended) {
  return (
    dispatchColumnMapExtended[columnKey]?.sortType === "number" ||
    (typeof value === "number" && Number.isFinite(value))
  );
}

export function EmailTriagePage({ alertType }) {
  useRegisterAiChatScreen(true);
  const location = useLocation();
  const { accounts } = useMsal();
  const activeAccount = accounts[0] ?? null;
  const userPrefsRef = useRef(null);
  const userScope = useMemo(
    () => getUserSiteScopeFromRmpmAlertStorage(),
    [activeAccount?.username],
  );

  const normalizedUserScope = useMemo(
    () => normalizeUserScope(userScope),
    [userScope],
  );
  const scopeLocked = hasUserScope(normalizedUserScope);

  const [kpis, setKpis] = useState([
    {
      catagory: "",
      count: 0,
      sub_catagory: [
        {
          catagory: "",
          count: 0
        }

      ]
    },
  ]);

  const [filterOptions, setFilterOptions] = useState({
    po_number: [],
    unit_code: [],
    site_cluster: [],
    vendor_name: [],
    supplier_inventory: [],
    material_code: [],
    material_desc: [],
    dispatch_status: [],
    week: [],
    no_of_day_delayed: [],
    format: [],
    bg: [],
  });

  const [selectedFilters, setSelectedFilters] = useState(() =>
    applyUserScopeToFilters(
      loadPersistedSelectedFilters(
        EMAIL_TRIAGE_SELECTED_FILTERS_STORAGE_KEY,
        buildInitialSelectedFilters(normalizedUserScope),
      ),
      normalizedUserScope,
    ),
  );

  const effectiveFilters = useMemo(() => {
    const scoped = applyUserScopeToFilters(selectedFilters, normalizedUserScope);
    if (!Array.isArray(scoped.dispatch_status) || scoped.dispatch_status.length === 0) {
      return scoped;
    }
    return {
      ...scoped,
      dispatch_status: normalizeDispatchStatusFilterApiValues(scoped.dispatch_status),
    };
  }, [selectedFilters, normalizedUserScope]);

  const monitorTodayMaterial = String(location?.state?.monitorTodayContext?.material ?? "").trim();
  const monitorTodayPlant = String(location?.state?.monitorTodayContext?.plant ?? "").trim();
  const monitorTodayDeliveryStatus = useMemo(() => {
    const rawDeliveryStatus = location?.state?.monitorTodayContext?.deliveryStatus;

    if (Array.isArray(rawDeliveryStatus)) {
      return rawDeliveryStatus
        .map((status) => String(status ?? "").trim())
        .filter(Boolean);
    }

    const singleStatus = String(rawDeliveryStatus ?? "").trim();
    return singleStatus ? [singleStatus] : [];
  }, [location?.state?.monitorTodayContext?.deliveryStatus]);

  const kpiFilters = useMemo(() => {
    const { dispatch_status: _dispatchStatus, ...rest } = effectiveFilters;
    return rest;
  }, [effectiveFilters]);

  useEffect(() => {
    setSelectedFilters((prev) =>
      applyUserScopeToFilters(prev, normalizedUserScope),
    );
  }, [normalizedUserScope]);

  useEffect(() => {
    if (!monitorTodayMaterial && !monitorTodayPlant && monitorTodayDeliveryStatus.length === 0) {
      return;
    }

    const redirectedFilters = buildInitialSelectedFilters(normalizedUserScope);

    redirectedFilters.material_code = monitorTodayMaterial ? [monitorTodayMaterial] : [];
    redirectedFilters.unit_code = monitorTodayPlant ? [monitorTodayPlant] : [];
    redirectedFilters.dispatch_status = monitorTodayDeliveryStatus.length > 0
      ? monitorTodayDeliveryStatus
      : [];

    const normalizedRedirectedFilters = applyUserScopeToFilters(
      redirectedFilters,
      normalizedUserScope,
    );

    setSelectedFilters(normalizedRedirectedFilters);

    const redirectLabelSelections = {
      [generateLabel("material_code")]: monitorTodayMaterial ? [monitorTodayMaterial] : [],
      [generateLabel("unit_code")]: monitorTodayPlant ? [monitorTodayPlant] : [],
      [generateLabel("dispatch_status")]: toDisplayEmailTriageFilterValues(
        "dispatch_status",
        monitorTodayDeliveryStatus,
      ),
    };

    setRmpmFilters((prev) =>
      prev.map((filter) => ({
        ...filter,
        selectedData: redirectLabelSelections[filter.label] ?? [],
      })),
    );

    setCurrentPage(1);
  }, [monitorTodayMaterial, monitorTodayPlant, monitorTodayDeliveryStatus, normalizedUserScope]);

  useEffect(() => {
    savePersistedSelectedFilters(EMAIL_TRIAGE_SELECTED_FILTERS_STORAGE_KEY, selectedFilters);
  }, [selectedFilters]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState({});

  const [alertDetailsForInsight, setAlertDetailsForInsight] = useState(null);

  useEffect(() => {
    if (!openDrawer) return undefined;
    const fallbackDetails = mapDispatchRowToRmpmAlertDetails(selectedAlert);
    const alertId = fallbackDetails["Alert ID"];
    let cancelled = false;
    setAlertDetailsForInsight(null);
    (async () => {
      let matchedAlert;
      try {
        const response = await alertDetailAPI(
          buildRmpmAlertLookupPayload(fallbackDetails, {
            email: activeAccount?.username,
            role: activeAccount?.idTokenClaims?.roles,
          }),
        );
        const alertDetails = Array.isArray(response?.alertDetails) ? response.alertDetails : [];
        matchedAlert = alertDetails.find(
          (alert) => String(alert?.["Alert ID"] ?? "").trim() === alertId,
        );
      } catch (error) {
        console.error("Failed to fetch RMPM alert details:", error);
      }
      if (!cancelled) setAlertDetailsForInsight(matchedAlert ?? fallbackDetails);
    })();
    return () => {
      cancelled = true;
    };
  }, [openDrawer, selectedAlert, activeAccount?.username]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await emailTriageGetFilters(effectiveFilters);
        if (!cancelled && data) setFilterOptions((prev) => ({ ...prev, ...data }));
      } catch (error) {
        console.error("Failed to fetch filters:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveFilters]);

  const [tableData, setTableData] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState({ poNumber: "", materialId: "", vendorName: "", rowId: "" });
  const [emailListTab, setEmailListTab] = useState("drafts");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [initialPageLoading, setInitialPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [emailModalLoading, setEmailModalLoading] = useState(false);
  const [deleteErrorSnackbar, setDeleteErrorSnackbar] = useState("");
  const composerMailSourceRef = useRef(null);
  const tableFetchSeqRef = useRef(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [dispatchColumnList, setDispatchColumnList] = useState(buildDispatchColumnPreferenceList);
  const [columnsPrefsVersion, setColumnsPrefsVersion] = useState(0);
  const [customiseColumnsOpen, setCustomiseColumnsOpen] = useState(false);
  const [rmpmFilters, setRmpmFilters] = useState([]);
  const [userPrefsFilters, setUserPrefsFilters] = useState(null);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [vendorGrouped, setVendorGrouped] = useState(false);
  const [historyDraftEmails, setHistoryDraftEmails] = useState([]);
  const [historyInboxEmails, setHistoryInoboxEmails] = useState([]);
  const [historySentEmails, setHistorySentEmails] = useState([]);
  const [replyNowAutoCompose, setReplyNowAutoCompose] = useState(false);
  const [tripIds, setTripIds] = useState([]);
  const [tripIdModalOpen, setTripIdModalOpen] = useState(false);

  useEffect(() => {
    if (!emailModalOpen) {
      composerMailSourceRef.current = null;
      setReplyNowAutoCompose(false);
      setIsReplyNowCompose(false);
    }
  }, [emailModalOpen]);

  useEffect(() => {
    const prefs = userPrefsRef.current?.columns ?? [];
    setDispatchColumnList(mapUserPrefsToColumnList(prefs));
  }, [columnsPrefsVersion]);

  const dispatchColumnMapExtended = useMemo(() => {
    const ext = { ...DISPATCH_COLUMN_MAP };
    for (const p of dispatchColumnList) {
      if (!ext[p.column]) {
        ext[p.column] = {
          key: p.column,
          label: p.label,
          sortType: "string",
          sortable: true,
        };
      }
    }
    return ext;
  }, [dispatchColumnList]);

  const scopedFilterOptions = useMemo(() => {
    if (!normalizedUserScope.filterKey || !normalizedUserScope.values.length) {
      return null;
    }
    return {
      key: normalizedUserScope.filterKey,
      values: [
        ...new Set(
          normalizedUserScope.values
            .map((v) => String(v ?? "").trim())
            .filter(Boolean),
        ),
      ],
    };
  }, [normalizedUserScope]);

  const getFilterDropdownOptions = useCallback(
    (key) => {
      if (scopedFilterOptions && key === scopedFilterOptions.key) {
        return scopedFilterOptions.values;
      }
      return formatEmailTriageFilterOptionsForDisplay(key, filterOptions[key] || []);
    },
    [filterOptions, scopedFilterOptions],
  );

  const rmpmFiltersOption = useMemo(() => {
    const o = {};
    Object.keys(filterOptions).forEach((key) => {
      o[generateLabel(key)] = getFilterDropdownOptions(key);
    });
    return o;
  }, [filterOptions, getFilterDropdownOptions]);

  useEffect(() => {
    if (userPrefsFilters === null) return;

    const sorted = [...userPrefsFilters].sort((a, b) => {
      const pa = Number(a?.priority);
      const pb = Number(b?.priority);
      const da = Number.isFinite(pa) ? pa : 999;
      const db = Number.isFinite(pb) ? pb : 999;
      return da - db;
    });

    const nextSelected = { ...selectedFilters };
    sorted.forEach((pf) => {
      const key = resolvePrefFilterNameToKey(pf.name);
      if (key && nextSelected[key] === undefined) nextSelected[key] = [];
    });
    setSelectedFilters(
      applyUserScopeToFilters(nextSelected, normalizedUserScope),
    );

    setRmpmFilters((prev) => {
      const prevByLabel = Object.fromEntries(prev.map((f) => [f.label, f]));
      return sorted
        .map((pf, idx) => {
          const key = resolvePrefFilterNameToKey(pf.name);
          if (!key) return null;
          const label = generateLabel(key);
          const po = Number(pf.priority);
          const displayOrder = Number.isFinite(po) ? po : idx + 1;
          return {
            label,
            selectedData:
              prevByLabel[label]?.selectedData ?? selectedFilters[key] ?? [],
            isFavourite: normalizePrefIsActive(pf.is_active),
            DisplayOrder: displayOrder,
          };
        })
        .filter(Boolean);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrefsFilters, normalizedUserScope]);

  const fetchKPIs = useCallback(async () => {
    try {
      const data = await emailTriageGetKpis(kpiFilters);
      if (data) setKpis(data);
    } catch (error) {
      console.error("Failed to fetch KPIs:", error);
    }
  }, [kpiFilters]);

  const fetchUserPrefs = async (email) => {
    if (!email) return;
    try {
      const data = await emailTriageGetUserPrefs(email, USER_PREFS_SCREEN_ID);
      userPrefsRef.current = data;
      if (data) {
        setColumnsPrefsVersion((v) => v + 1);
      }
      const filtersFromApi = Array.isArray(data?.filters) ? data.filters : [];
      setUserPrefsFilters(filtersFromApi);
      if (filtersFromApi.length > 0) {
        setFilterOptions((prev) => {
          const updated = { ...prev };
          let changed = false;
          for (const pf of filtersFromApi) {
            const key = resolvePrefFilterNameToKey(pf.name);
            if (key && !(key in updated)) {
              updated[key] = [];
              changed = true;
            }
          }
          return changed ? updated : prev;
        });
      }
    } catch (err) {
      console.error("Failed to fetch user prefs (using defaults):", err);
      setUserPrefsFilters([]);
    }
  };

  const saveUserPrefs = useCallback(
    async (columns, filters) => {
      const email = activeAccount?.username;
      if (!email) return;
      try {
        const payload = buildUserPrefsPayload(email, columns, filters, userPrefsFilters, filterOptions);
        console.log(payload, "payload");
        await emailTriageSaveUserPrefs(payload);
      } catch (err) {
        console.error("Failed to save user preferences:", err);
      }
    },
    [activeAccount?.username, filterOptions],
  );

  const fetchTableData = async ({ page, pageSize, filters, grouped, sort, requestId } = {}) => {
    try {
      const fetcher = grouped ? emailTriageGetDispatchesV2 : emailTriageGetDispatches;
      const activeSort =
        sort ??
        (sortConfig.key
          ? [{ key: sortConfig.key, direction: sortConfig.direction }]
          : undefined);
      const data = await fetcher({
        page: page ?? currentPage,
        pageSize: pageSize ?? itemsPerPage,
        filters: filters ?? effectiveFilters,
        sort: activeSort,
      });
      if (requestId != null && requestId !== tableFetchSeqRef.current) return;
      if (data) {
        setTableData(
          Array.isArray(data.items) ? data.items.map((row) => normalizeDispatchRow(row)) : [],
        );
        setTotalItems(data.total);
        setTotalPages(data.totalPages);
        setItemsPerPage(data.pageSize);
        setCurrentPage(data.page);
      }
    } catch (error) {
      console.error("Failed to fetch table data:", error);
    }
  };

  const fetchEmailHistory = async (vendorName) => {
    try {
      const data = await emailTriageGetEmailHistory(vendorName);
      if (data) {
        setHistoryDraftEmails(Array.isArray(data.drafts) ? data.drafts : []);
        setHistoryInoboxEmails(Array.isArray(data.inbox) ? data.inbox : []);
        setHistorySentEmails(Array.isArray(data.sent_mails) ? data.sent_mails : []);
      }
      return data;
    } catch (error) {
      console.error("Failed to fetch email history:", error);
      return null;
    }
  };

  const refreshEmailHistoryForModal = useCallback(async () => {
    const vendorName = selectedPO?.vendorName ?? "";
    if (!vendorName) return;
    await fetchEmailHistory(vendorName);
  }, [selectedPO?.vendorName]);

  const removeLocalDraftEmail = useCallback((email) => {
    setHistoryDraftEmails((prev) => prev.filter((row) => row !== email));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const email = activeAccount?.username ?? "";
    (async () => {
      setInitialPageLoading(true);
      try {
        await fetchUserPrefs(email);
      } finally {
        if (!cancelled) setInitialPageLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchKPIs();
  }, [effectiveFilters]);

  useEffect(() => {
    let cancelled = false;
    const requestId = tableFetchSeqRef.current + 1;
    tableFetchSeqRef.current = requestId;
    (async () => {
      setTableLoading(true);
      try {
        await fetchTableData({
          page: currentPage,
          pageSize: itemsPerPage,
          filters: effectiveFilters,
          grouped: vendorGrouped,
          requestId,
        });
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, effectiveFilters, vendorGrouped, sortConfig]);

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages || 1)));
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const applyDispatchStatusFilter = useCallback((dispatchValues) => {
    const nextValues = Array.isArray(dispatchValues) ? dispatchValues : [];
    const displayValues = toDisplayEmailTriageFilterValues("dispatch_status", nextValues);
    const dispatchLabel = generateLabel("dispatch_status");

    setCurrentPage(1);
    setSelectedFilters((prev) =>
      applyUserScopeToFilters(
        { ...prev, dispatch_status: nextValues },
        normalizedUserScope,
      ),
    );
    setRmpmFilters((prev) =>
      prev.map((f) =>
        f.label === dispatchLabel ? { ...f, selectedData: displayValues } : f,
      ),
    );
  }, [normalizedUserScope]);

  const handleKpiFilterClick = useCallback((mode) => {
    applyDispatchStatusFilter(KPI_DISPATCH_STATUS_FILTERS[mode] || []);
  }, [applyDispatchStatusFilter]);

  const handleKpiBreakdownClick = useCallback((label) => {
    applyDispatchStatusFilter([label]);
  }, [applyDispatchStatusFilter]);

  const handleTotalKpiBreakdownClick = useCallback((metric) => {
    const mode = TOTAL_KPI_BREAKDOWN_MODES[metric.label];
    if (mode) handleKpiFilterClick(mode);
  }, [handleKpiFilterClick]);

  const kpiCategoryBreakdownConfig = useCallback(
    (categoryMode) => ({
      onBreakdownClick: (metric) => handleKpiBreakdownClick(metric.label),
      isBreakdownActive: (label) =>
        isCategoryKpiBreakdownActive(selectedFilters.dispatch_status, label, categoryMode),
    }),
    [handleKpiBreakdownClick, selectedFilters.dispatch_status],
  );

  const kpiTotalBreakdownConfig = useMemo(
    () => ({
      onBreakdownClick: handleTotalKpiBreakdownClick,
      isBreakdownActive: (label) => {
        const mode = TOTAL_KPI_BREAKDOWN_MODES[label];
        return mode
          ? isTotalKpiBreakdownActive(selectedFilters.dispatch_status, mode)
          : false;
      },
    }),
    [handleTotalKpiBreakdownClick, selectedFilters.dispatch_status],
  );

  const kpiTotalActive = isKpiCardFilterActive(selectedFilters.dispatch_status, "all");
  const kpiPreponedActive = isKpiCardFilterActive(selectedFilters.dispatch_status, "preponed");
  const kpiOnTimeActive = isKpiCardFilterActive(selectedFilters.dispatch_status, "on_time");
  const kpiDelayedActive = isKpiCardFilterActive(selectedFilters.dispatch_status, "delayed");

  const kpiPreponedMainActive = hasKpiDispatchStatusFilter(
    selectedFilters.dispatch_status,
    KPI_DISPATCH_STATUS_FILTERS.preponed,
  );
  const kpiOnTimeMainActive = hasKpiDispatchStatusFilter(
    selectedFilters.dispatch_status,
    KPI_DISPATCH_STATUS_FILTERS.on_time,
  );
  const kpiDelayedMainActive = hasKpiDispatchStatusFilter(
    selectedFilters.dispatch_status,
    KPI_DISPATCH_STATUS_FILTERS.delayed,
  );

  const isScopeFilterLocked = useCallback(
    (key) => {
      if (!scopeLocked || !normalizedUserScope.filterKey) return false;
      return (
        key === normalizedUserScope.filterKey &&
        normalizedUserScope.values.length > 0
      );
    },
    [scopeLocked, normalizedUserScope],
  );

  const handleMultiSelectChange = useCallback((label, values) => {
    const key = Object.keys(filterOptions).find((k) => generateLabel(k) === label);
    if (!key || isScopeFilterLocked(key)) return;
    const nextDisplayVals = Array.isArray(values) ? values : [];
    const nextApiVals = toApiEmailTriageFilterValues(key, nextDisplayVals);
    setSelectedFilters((prev) => ({ ...prev, [key]: nextApiVals }));
    setRmpmFilters((prev) =>
      prev.map((f) => (f.label === label ? { ...f, selectedData: nextDisplayVals } : f)),
    );
    setCurrentPage(1);
  }, [filterOptions, isScopeFilterLocked]);

  const resetFilterItem = (f) => {
    const key = Object.keys(filterOptions).find(
      (k) => generateLabel(k) === f.label
    );

    if (!key) {
      return { ...f, selectedData: [] };
    }

    if (
      key === normalizedUserScope.filterKey &&
      normalizedUserScope.values.length > 0
    ) {
      return {
        ...f,
        selectedData: [...normalizedUserScope.values],
      };
    }

    return { ...f, selectedData: [] };
  };

  const resetFilter = useCallback(() => {
    const cleared = buildInitialSelectedFilters(normalizedUserScope);

    clearPersistedSelectedFilters(
      EMAIL_TRIAGE_SELECTED_FILTERS_STORAGE_KEY
    );

    setSelectedFilters(cleared);

    savePersistedSelectedFilters(
      EMAIL_TRIAGE_SELECTED_FILTERS_STORAGE_KEY,
      cleared
    );

    setRmpmFilters((prev) => prev.map(resetFilterItem));

    setCurrentPage(1);
    setSortConfig({ key: null, direction: "asc" });
  }, [normalizedUserScope, filterOptions]);

  const buildSelectedFilters = (
    prev,
    updatedFilters,
    filterOptions,
    normalizedUserScope
  ) => {
    const next = { ...prev };

    updatedFilters.reduce((acc, f) => {
      const key = Object.keys(filterOptions).find(
        (k) => generateLabel(k) === f.label
      );

      if (key) {
        acc[key] = toApiEmailTriageFilterValues(
          key,
          f.selectedData || []
        );
      }

      return acc;
    }, next);

    return applyUserScopeToFilters(next, normalizedUserScope);
  };

  const handleFiltersSaved = useCallback(
    (updatedFilters) => {
      setRmpmFilters(updatedFilters);

      setSelectedFilters((prev) =>
        buildSelectedFilters(
          prev,
          updatedFilters,
          filterOptions,
          normalizedUserScope
        )
      );

      setCurrentPage(1);
      saveUserPrefs(dispatchColumnList, updatedFilters);
    },
    [
      filterOptions,
      saveUserPrefs,
      dispatchColumnList,
      normalizedUserScope,
    ]
  );

  const mapFilterForDrawer = (filter) => {
    const key = Object.keys(filterOptions).find(
      (k) => generateLabel(k) === filter.label
    );

    if (!key) {
      return filter;
    }

    return {
      ...filter,
      selectedData: toDisplayEmailTriageFilterValues(
        key,
        selectedFilters[key] || []
      ),
    };
  };

  const openMoreFiltersDrawer = useCallback(() => {
    setRmpmFilters((prev) => prev.map(mapFilterForDrawer));
    setFilterDrawer(true);
  }, [filterOptions, selectedFilters]);

  const closeMoreFiltersDrawer = useCallback(() => {
    setFilterDrawer(false);
  }, []);

  const { startItem, endItem } = useMemo(() => {
    if (totalItems === 0) {
      return {
        startItem: 0,
        endItem: 0,
      };
    }

    return {
      startItem: (currentPage - 1) * itemsPerPage + 1,
      endItem: Math.min(currentPage * itemsPerPage, totalItems),
    };
  }, [currentPage, itemsPerPage, totalItems]);

  const handleColumnSort = useCallback((columnKey, forcedDirection) => {
    const col = dispatchColumnMapExtended[columnKey];
    if (!col || col.sortable === false) return;
    setCurrentPage(1);
    setSortConfig((prev) => {
      const sameColumn = prev.key === columnKey;
      // Re-clicking the applied direction clears sorting.
      if (forcedDirection) {
        return sameColumn && prev.direction === forcedDirection
          ? { key: null, direction: "asc" }
          : { key: columnKey, direction: forcedDirection };
      }
      if (!sameColumn) return { key: columnKey, direction: "asc" };
      return prev.direction === "asc"
        ? { key: columnKey, direction: "desc" }
        : { key: null, direction: "asc" };
    });
  }, [dispatchColumnMapExtended]);

  const visibleDispatchColumns = useMemo(() => {
    return [...dispatchColumnList]
      .filter((c) => c.visibility === true)
      .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0));
  }, [dispatchColumnList]);

  const vendorGroups = useMemo(() => {
    if (!vendorGrouped) return [];
    return groupRowsByVendor(tableData);
  }, [vendorGrouped, tableData]);

  const getDispatchRowKey = getEmailTriageDispatchRowKey;

  /** Checkbox selection in grouped table (all vendors on current page). */
  const [groupTableSelectedKeys, setGroupTableSelectedKeys] = useState(() => new Set());
  /** Rows for the vendor whose Reply Now was clicked (passed to email composer). */
  const [selectedDispatchRows, setSelectedDispatchRows] = useState([]);
  const [isReplyNowCompose, setIsReplyNowCompose] = useState(false);

  useEffect(() => {
    if (!vendorGrouped) {
      setGroupTableSelectedKeys(new Set());
      return;
    }
    if (!tableData.length) {
      setGroupTableSelectedKeys(new Set());
      return;
    }
    setGroupTableSelectedKeys(
      new Set(tableData.map((row) => getDispatchRowKey(row))),
    );
  }, [vendorGrouped, tableData, getDispatchRowKey]);

  const toggleDispatchRow = useCallback((rowKey) => {
    setGroupTableSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const handleSelectAllRows = useCallback((rows) => {
    const rowKeys = rows.map((row) => getDispatchRowKey(row));
    setGroupTableSelectedKeys((prev) => {
      const allSelected =
        rowKeys.length > 0 && rowKeys.every((key) => prev.has(key));
      const next = new Set(prev);
      if (allSelected) {
        rowKeys.forEach((key) => next.delete(key));
      } else {
        rowKeys.forEach((key) => next.add(key));
      }
      return next;
    });
  }, [getDispatchRowKey]);

  const handleReplyNow = useCallback(async (po, materialId, vendorName = "", groupRows = null, rowId = null) => {
    let rowsForReply = [];
    if (vendorGrouped && Array.isArray(groupRows) && groupRows.length > 0) {
      const checkedRows = groupRows.filter((row) =>
        groupTableSelectedKeys.has(getDispatchRowKey(row)),
      );
      rowsForReply = checkedRows.length > 0 ? checkedRows : [...groupRows];
    } else {
      const matchedRow = tableData.find(
        (row) =>
          String(row.poNumber ?? "") === String(po ?? "") &&
          String(row.materialCode ?? "") === String(materialId ?? ""),
      );
      rowsForReply = matchedRow
        ? [matchedRow]
        : [{ poNumber: po, materialCode: materialId, vendorName, id: rowId, rowId }];
    }

    setSelectedDispatchRows(rowsForReply);
    setIsReplyNowCompose(true);
    setEmailModalOpen(true);
    setSelectedPO({ poNumber: po, materialId, vendorName, rowId });
    setEmailListTab("drafts");
    setEmailModalLoading(true);
    try {
      await fetchEmailHistory(vendorName);
    } finally {
      setEmailModalLoading(false);
    }
  }, [vendorGrouped, groupTableSelectedKeys, getDispatchRowKey, tableData]);

  const handleReplyNowAutoComposeComplete = useCallback(() => {
    setReplyNowAutoCompose(false);
  }, []);

  const tableColumnsForRender = visibleDispatchColumns;
  const vendorColVisible = useMemo(
    () => tableColumnsForRender.some((c) => c.column === "vendorName"),
    [tableColumnsForRender],
  );
  const groupedSelectionColumnCount = vendorGrouped ? 1 : 0;

  const totalColumnsForGroupedTable =
    tableColumnsForRender.length + groupedSelectionColumnCount;

  const openCustomiseColumnsDrawer = useCallback(() => {
    setCustomiseColumnsOpen(true);
  }, []);

  const handleDispatchColumnCustomization = useCallback((list) => {
    if (Array.isArray(list)) setDispatchColumnList(list);
    setCustomiseColumnsOpen(false);
    if (Array.isArray(list)) {
      saveUserPrefs(list, rmpmFilters);
    }
  }, [saveUserPrefs, rmpmFilters]);

  const closeCustomiseColumnsDrawer = useCallback(() => {
    setCustomiseColumnsOpen(false);
  }, []);

  const exportToExcel = useCallback(async () => {
    try {
      const data = await emailTriageExportData(effectiveFilters);
      const downloadUrl = typeof data === "string" ? data : data?.url ?? data?.download_url;
      if (!downloadUrl) {
        console.error("Export API did not return a download URL");
        return;
      }
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Export failed:", err);
    }
  }, [effectiveFilters]);

  const overflowMenuItems = useMemo(
    () => [
      { label: "More Filters", icon: Filters, onClick: openMoreFiltersDrawer },
      { label: "Customise", icon: Slider, onClick: openCustomiseColumnsDrawer },
      {
        label: "Export",
        icon: Excel,
        onClick: () => {
          void exportToExcel();
        },
      },
    ],
    [exportToExcel, openMoreFiltersDrawer, openCustomiseColumnsDrawer],
  );

  const toggleDrawer = (open, alertId) => () => {
    if (open) {
      setSelectedAlert(alertId);
    }
    setOpenDrawer(open);
  };

  const handleTripIdClick = useCallback((ids) => {
    const parsed = parseTripIds(ids);
    if (parsed.length === 0) return;
    setTripIds(parsed);
    setTripIdModalOpen(true);
  }, [setTripIds, setTripIdModalOpen]);

  function renderEtaBadgeCell(row, columnKey, cellTitle, raw) {
    if (!raw) {
      return <td key={columnKey} title={cellTitle}>-</td>;
    }

    return (
      <td key={columnKey} title={cellTitle}>
        <span
          className={`status ${getDispatchStatusBadgeClass(
            row.dispatchStatus,
          )}`}
        >
          {formatDisplayDdMmYy(raw) || "-"}
        </span>
      </td>
    );
  }

  function renderCriticalityCell(
    row,
    columnKey,
    cellTitle,
    tdClass,
  ) {
    const ciNum = Number(row.criticalityIndex);

    if (
      row.criticalityIndex == null ||
      row.criticalityIndex === "" ||
      !Number.isFinite(ciNum)
    ) {
      return <td key={columnKey} title={cellTitle}>-</td>;
    }

    const barColor = ciNum >= 80 ? "#dc2626" : "#ca8a04";

    return (
      <td key={columnKey} className={tdClass()} title={cellTitle}>
        <Box
          sx={{
            minWidth: 72,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {Math.round(ciNum)}%
          </Typography>

          <Box
            sx={{
              width: "100%",
              height: 6,
              bgcolor: "action.hover",
              borderRadius: 0.5,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${ciNum}%`,
                height: "100%",
                bgcolor: barColor,
                borderRadius: 0.5,
              }}
            />
          </Box>
        </Box>
      </td>
    );
  }

  function renderTripIdCell(
    row,
    columnKey,
    tdClass,
    handleTripIdClick,
  ) {
    const tripIds = parseTripIds(row.trip_id);
    const display = tripIds.length ? tripIds.join(",") : "-";

    return (
      <td
        key={columnKey}
        className={tdClass()}
        style={{
          fontWeight: 700,
          fontSize: "10px",
          cursor: tripIds.length ? "pointer" : "default",
          color: tripIds.length ? "#2563eb" : undefined,
        }}
        onClick={() => {
          if (tripIds.length) {
            handleTripIdClick(tripIds);
          }
        }}
      >
        <span
          className="email-triage-trip-id"
          style={{
            textDecoration: tripIds.length
              ? "underline"
              : "none",
          }}
          title={tripIds.length ? display : undefined}
        >
          {display}
        </span>
      </td>
    );
  }

  function renderDispatchStatusCell(
    row,
    columnKey,
    cellTitle,
  ) {
    const statusTooltip = getDispatchStatusValueTooltip(row.dispatchStatus);
    return (
      <td key={columnKey} title={statusTooltip || cellTitle}>
        <span
          className={`status ${getDispatchStatusBadgeClass(
            row.dispatchStatus,
          )}`}
          title={statusTooltip}
        >
          {getDispatchStatusDisplayLabel(row.dispatchStatus)}
        </span>
      </td>
    );
  }

  function renderAlertIdCell(
    row,
    columnKey,
    cellTitle,
    setSelectedAlert,
    setOpenDrawer,
  ) {
    const openAlert = () => {
      if (!row.alertId) return;
      setSelectedAlert(row);
      setOpenDrawer(true);
    };

    return (
      <td
        key={columnKey}
        title={cellTitle}
        onClick={openAlert}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span
            style={{
              color: "#353535",
              fontWeight: 600,
              fontSize: "9px",
            }}
          >
            {row.alertId || "-"}
          </span>

          <button
            type="button"
            className="link"
            style={{
              cursor: row.alertId ? "pointer" : "default",
              fontSize: 11,
              color: row.alertId ? undefined : "#b7b7b7", padding: 0,
              border: 0,
              background: "transparent",
            }}
            onClick={(event) => {
              event.stopPropagation();
              openAlert();
            }}
            disabled={!row.alertId}
          >
            More Details
          </button>
        </div>
      </td>
    );
  }

  function renderReplyNowCell(
    row,
    columnKey,
    stickyClass,
    handleReplyNow,
  ) {
    const actionDisabled = isDispatchOnTimeStatus(
      row.dispatchStatus,
    );

    return (
      <td
        key={columnKey}
        className={stickyClass}
        title="Reply Now"
      >
        <button
          type="button"
          className="reply-btn"
          title={
            actionDisabled
              ? "Draft mail is not available for on-time dispatches"
              : undefined
          }
          onClick={() =>
            handleReplyNow(
              row.poNumber,
              row.materialCode,
              row.vendorName,
              null,
              row.id ?? row.row_id ?? row.rowId,
            )
          }
        >
          <Sparkles size={12} />
          Reply Now
        </button>
      </td>
    );
  }


  function renderDefaultCell(
    row,
    columnKey,
    cellTitle,
    dispatchColumnMapExtended,
  ) {
    const value = row[columnKey];

    return (
      <td
        key={columnKey}
        className={
          isNumericCell(value, columnKey, dispatchColumnMapExtended)
            ? "email-triage-td-numeric"
            : undefined
        }
        title={cellTitle}
      >
        {String(value ?? "")}
      </td>
    );
  }

  const renderDispatchBodyCell = (row, columnKey) => {
    const stickyClass = vendorGrouped ? "" : STICKY_CLASS_MAP[columnKey] || "";
    const tdNum = dispatchColumnMapExtended[columnKey]?.sortType === "number" ? "email-triage-td-numeric" : "";
    const cellTitle = getDispatchCellTitle(row, columnKey) || undefined;
    const tdClass = (...parts) => {
      const s = [...parts, tdNum].filter(Boolean).join(" ");
      return s || undefined;
    };
    switch (columnKey) {
      case "srNo":
        return <td key={columnKey} className={tdClass("strong", stickyClass)} title={cellTitle}>{row.srNo}</td>;
      case "poNumber":
        return <td key={columnKey} title={cellTitle}><span className="link">{row.poNumber}</span></td>;
      case "unitCode":
        return <td key={columnKey} title={cellTitle}>{row.unitCode}</td>;
      case "vendorName":
        return <td key={columnKey} title={cellTitle}>{row.vendorName}</td>;
      case "supplierInventory": {
        const inventory = row.supplierInventory;
        const numericInventory = Number(inventory);

        const formattedInventory =
          inventory != null &&
            inventory !== "" &&
            Number.isFinite(numericInventory)
            ? numericInventory.toLocaleString()
            : "";

        return (
          <td
            key={columnKey}
            className={tdClass("strong")}
            title={
              formattedInventory
                ? `${formattedInventory}${row.uom ? ` ${row.uom}` : ""}`
                : undefined
            }
          >
            {formattedInventory
              ? `${formattedInventory}${row.uom ? ` ${row.uom}` : ""}`
              : "-"}
          </td>
        );
      }
      case "materialCode":
        return <td key={columnKey} className="strong" title={cellTitle}>{row.materialCode}</td>;
      case "materialDescription":
        return <td key={columnKey} title={cellTitle}>{row.materialDescription}</td>;
      case "qtyPlanned": {
        const qty = row.qtyPlanned?.toLocaleString?.() ?? row.qtyPlanned;
        if (qty == null || qty === "") return <td key={columnKey} className={tdClass("strong")} title={cellTitle}>-</td>;
        return (
          <td key={columnKey} className={tdClass("strong")} title={cellTitle}>
            {qty}{row.uom ? ` ${row.uom}` : ""}
          </td>
        );
      }
      case "qtyAvailable":
        return <td key={columnKey} className={tdClass("strong")} title={cellTitle}>{Number(row.qtyAvailable).toLocaleString()}{row.uom ? ` ${row.uom}` : ""}</td>;
      case "originalDispatchDate":
        return <td key={columnKey} title={cellTitle}>{formatDisplayDdMmYy(row.originalDispatchDate) || "-"}</td>;
      case "originalDeliveryDate":
        return <td key={columnKey} title={cellTitle}>{formatDisplayDdMmYy(row.originalDeliveryDate) || "-"}</td>;
      case "updatedDispatchDate":
        return <td key={columnKey} className="link" title={cellTitle}>{formatDisplayDdMmYy(row.updatedDispatchDate) || "-"}</td>;
      case "updatedDeliveryDate":
        return <td key={columnKey} className="link" title={cellTitle}>{formatDisplayDdMmYy(row.updatedDeliveryDate) || "-"}</td>;
      case "eta":
        return <td key={columnKey} title={cellTitle}>{formatDisplayDdMmYy(row.eta) || "-"}</td>;
      case "updatedEta":
        return renderEtaBadgeCell(row, columnKey, cellTitle, row.updatedEta);
      case "criticalityIndex":
        return renderCriticalityCell(
          row,
          columnKey,
          cellTitle,
          tdClass,
        );
      case "rca":
        return (
          <td
            key={columnKey}
            title={getRootCauseValueTooltip(row.rca) || cellTitle}
          >
            {row.rca || "-"}
          </td>
        );
      case "shortageDate": {
        return (
          <td key={columnKey} className="text-center" title={cellTitle}>
            {row.shortageDate || "-"}
          </td>
        );
      }
      case "alertId":
        return renderAlertIdCell(
          row,
          columnKey,
          cellTitle,
          setSelectedAlert,
          setOpenDrawer,
        );
      case "dispatchStatus":
        return renderDispatchStatusCell(
          row,
          columnKey,
          cellTitle,
        );
      case "noOfDayDelayed":
        return (
          <td key={columnKey} className={tdClass()} style={{ fontWeight: 700, fontSize: "10px" }} title={cellTitle}>
            {row.noOfDayDelayed != null ? `${row.noOfDayDelayed} Days` : "-"}
          </td>
        );
      case "alertWeek":
        return (
          <td key={columnKey} className={tdClass()} style={{ fontWeight: 700, fontSize: "10px" }} title={cellTitle}>
            {row.alertWeek || "-"}
          </td>
        );
      case "trip_id":
        return renderTripIdCell(
          row,
          columnKey,
          tdClass,
          handleTripIdClick,
        );
      case "last_communication":
      case "mailRefs":
        return renderLastCommunicationCell(row, columnKey);
      case "_action":
        return renderReplyNowCell(
          row,
          columnKey,
          stickyClass,
          handleReplyNow,
        );
      default:
        return renderDefaultCell(
          row,
          columnKey,
          cellTitle,
          dispatchColumnMapExtended,
        );

    }
  };
  function renderPagination() {
    if (tableLoading) {
      return <PaginationSkeleton />;
    }
    return (
      <div className="pagination-wrapper">
        <div className="pagination-left">
          <span className="pagination-text">Rows per page:</span>

          <div className="pagination-select-wrapper">
            <select
              value={itemsPerPage}
              onChange={(e) =>
                handleItemsPerPageChange(Number(e.target.value))
              }
              className="pagination-select"
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>

            <ChevronDown
              size={10}
              className="pagination-select-icon"
            />
          </div>

          <span className="pagination-text">
            {startItem}-{endItem} of {totalItems}
          </span>
        </div>

        <div className="pagination-right">
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="page-nav-btn"
          >
            <ChevronLeft
              size={14}
              className="text-gray-600"
            />
          </button>

          <div className="page-numbers">
            {renderPaginationPages()}
          </div>

          <button
            disabled={
              totalPages === 0 ||
              currentPage >= totalPages
            }
            onClick={() => handlePageChange(currentPage + 1)}
            className="page-nav-btn"
          >
            <ChevronRight
              size={14}
              className="text-gray-600"
            />
          </button>
        </div>
      </div>
    );
  }

  function renderPaginationPages() {
    return Array.from(
      { length: totalPages },
      (_, i) => i + 1,
    ).map((page) => {
      const showPage =
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 &&
          page <= currentPage + 1);

      const showEllipsis =
        page === currentPage - 2 ||
        page === currentPage + 2;

      if (showPage) {
        return (
          <button
            key={page}
            className={`page-btn ${currentPage === page ? "active" : ""
              }`}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </button>
        );
      }

      if (showEllipsis) {
        return (
          <span
            key={page}
            className="page-ellipsis"
          >
            ...
          </span>
        );
      }

      return null;
    });
  }

  function renderTableLoadingRows() {
    return (
      <TableBodySkeletonRows
        rows={skeletonTableRows}
        columns={totalColumnsForGroupedTable}
        rowClassName="table-row aps-table-skel-row"
        cellClassName="aps-table-skel-cell aps-table-skel-cell--email"
      />
    );
  }

  function renderEmptyTableRow() {
    return (
      <tr className="table-row">
        <td
          colSpan={Math.max(1, totalColumnsForGroupedTable)}
          className="email-triage-table-empty"
        >
          No data found
        </td>
      </tr>
    );
  }

  function isVendorGroupSelected(rows) {
    return (
      rows.length > 0 &&
      rows.every((row) =>
        groupTableSelectedKeys.has(getDispatchRowKey(row)),
      )
    );
  }

  function renderVendorGroupBanner(group, groupIndex, groupNoComm) {
    return (
      <tr
        key={`vendor-banner-${groupIndex}`}
        className="email-triage-group-banner"
      >
        <td colSpan={tableColumnsForRender.length}>
          <div className="email-triage-group-banner-inner">
            <label className="email-triage-checkbox" title="Select row">
              <input
                type="checkbox"
                checked={isVendorGroupSelected(group.rows)}
                onChange={() => handleSelectAllRows(group.rows)}
                aria-label={`Select all rows for vendor ${group.vendor}`}
              />
              <span className="email-triage-checkbox-box" aria-hidden />
            </label>

            <span className="email-triage-group-banner-title">
              {group.vendor}
            </span>

            {groupNoComm ? (
              <span className="email-triage-group-banner-note">
                No communications yet for this PO
              </span>
            ) : null}
          </div>
        </td>
      </tr>
    );
  }

  function renderGroupedVendorCell(row, rowIndex, groupSize) {
    if (!vendorColVisible || rowIndex > 0) {
      return null;
    }

    return (
      <td
        key="vendorName"
        rowSpan={groupSize}
        className="email-triage-vendor-cell"
        title={getDispatchCellTitle(row, "vendorName") || undefined}
      >
        {row.vendorName}
      </td>
    );
  }

  function renderGroupedActionCell({
    rowIndex,
    groupSize,
    actionStickyClass,
    groupActionDisabled,
    groupActionRow,
    group,
  }) {
    if (rowIndex > 0) {
      return null;
    }

    return (
      <td
        key="_action"
        rowSpan={groupSize}
        className={`email-triage-group-action-cell ${actionStickyClass}`.trim()}
        title="Reply Now"
      >
        <button
          type="button"
          className="reply-btn"
          title={
            groupActionDisabled
              ? "Draft mail is not available — every dispatch in this group is on time"
              : `Draft a reply to ${group.vendor}`
          }
          onClick={() =>
            handleReplyNow(
              groupActionRow.poNumber,
              groupActionRow.materialCode,
              groupActionRow.vendorName,
              group.rows,
            )
          }
        >
          <Sparkles size={12} />
          Reply Now
        </button>
      </td>
    );
  }

  function renderGroupedRowCell(pref, row, rowIndex, groupMeta, group) {
    if (pref.column === "vendorName") {
      return renderGroupedVendorCell(
        row,
        rowIndex,
        groupMeta.groupSize,
      );
    }

    if (pref.column === "_action") {
      return renderGroupedActionCell({
        rowIndex,
        groupSize: groupMeta.groupSize,
        actionStickyClass: groupMeta.actionStickyClass,
        groupActionDisabled: groupMeta.groupActionDisabled,
        groupActionRow: groupMeta.groupActionRow,
        group,
      });
    }

    return renderDispatchBodyCell(row, pref.column);
  }

  function renderVendorGroupedDataRow(group, groupIndex, row, rowIndex, groupMeta) {
    const rowKey = getDispatchRowKey(row);
    const checked = groupTableSelectedKeys.has(rowKey);

    return (
      <tr
        key={`${group.vendor}-${row.poNumber}-${row.materialCode}-${row.srNo}-${rowIndex}`}
        className={`table-row ${(groupIndex + rowIndex) % 2 ? "alt" : ""
          }${checked ? " table-row--selected" : ""}`}
      >
        <td className="email-triage-select-cell">
          <label className="email-triage-checkbox" title="Select row">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggleDispatchRow(rowKey)}
              aria-label={`Select row for PO ${row.poNumber}`}
            />
            <span className="email-triage-checkbox-box" aria-hidden />
          </label>
        </td>

        {tableColumnsForRender.map((pref) =>
          renderGroupedRowCell(pref, row, rowIndex, groupMeta, group),
        )}
      </tr>
    );
  }

  function renderVendorGroup(group, groupIndex) {
    const groupMeta = getVendorGroupMeta(group);

    return [
      renderVendorGroupBanner(
        group,
        groupIndex,
        groupMeta.groupNoComm,
      ),
      ...group.rows.map((row, rowIndex) =>
        renderVendorGroupedDataRow(
          group,
          groupIndex,
          row,
          rowIndex,
          groupMeta,
        ),
      ),
    ];
  }

  function renderGroupedTableRows() {
    return vendorGroups.flatMap((group, groupIndex) => {
      return renderVendorGroup(group, groupIndex);
    });
  }

  function renderFlatTableRows() {
    return tableData.map((row, index) => (
      <tr
        key={row.srNo}
        className={`table-row ${index % 2 ? "alt" : ""}`}
      >
        {tableColumnsForRender.map((pref) =>
          renderDispatchBodyCell(row, pref.column),
        )}
      </tr>
    ));
  }

  function renderDispatchTableBodyRows() {
    let rows;

    if (tableLoading) {
      rows = renderTableLoadingRows();
    } else if (tableData.length === 0) {
      rows = renderEmptyTableRow();
    } else if (vendorGrouped) {
      rows = renderGroupedTableRows();
    } else {
      rows = renderFlatTableRows();
    }

    return <>{rows}</>;
  }
  function renderDispatchHeaderCell(pref) {
    const col = dispatchColumnMapExtended[pref.column];

    if (!col) {
      return null;
    }

    const stickyClass =
      vendorGrouped && col.key !== "_action"
        ? ""
        : STICKY_CLASS_MAP[col.key] || "";

    const sortable = col.sortable !== false;

    const thNum =
      col.sortType === "number"
        ? "email-triage-th-numeric"
        : "";

    const thClass = [
      stickyClass,
      sortable ? "email-triage-th-sortable" : "",
      thNum,
    ]
      .filter(Boolean)
      .join(" ");

    let ariaSort;

    if (!sortable) {
      ariaSort = undefined;
    } else if (sortConfig.key !== col.key) {
      ariaSort = "none";
    } else if (sortConfig.direction === "asc") {
      ariaSort = "ascending";
    } else {
      ariaSort = "descending";
    }
    const tooltip = DISPATCH_COLUMN_TOOLTIPS[col.key];

    return (
      <th
        key={col.key}
        className={thClass || undefined}
        onClick={() => sortable && handleColumnSort(col.key)}
        scope="col"
        title={col.label}
        aria-sort={ariaSort}
      >
        <span className="email-triage-th-inner">
          <span className="email-triage-th-label">
            {col.label}
          </span>

          {tooltip ? (
            <span
              className="email-triage-th-info"
              title={tooltip}
              aria-label={tooltip}
            >
              <Info size={12} aria-hidden="true" />
            </span>
          ) : null}

          {sortable ? (
            <ColumnSortIcon
              direction={
                sortConfig.key === col.key
                  ? sortConfig.direction
                  : null
              }
              onSort={(dir) => handleColumnSort(col.key, dir)}
            />
          ) : null}
        </span>
      </th>
    );
  }

  function renderDispatchTableHeader() {
    return (
      <thead className="table-head">
        <tr>
          {vendorGrouped ? (
            <th
              className="email-triage-select-th"
              scope="col"
              aria-label="Select rows"
              title="Select rows"
            />
          ) : null}

          {tableColumnsForRender.map((pref) =>
            renderDispatchHeaderCell(pref),
          )}
        </tr>
      </thead>
    );
  }

  function renderDispatchTable() {
    return (
      <div className="table-agentic-wrapper">
        <div className="table-card">
          <div className="table-agentic-scroll">
            <table
              key={
                vendorGrouped
                  ? "dispatch-table-grouped"
                  : "dispatch-table-flat"
              }
              className={`table email-triage-dispatch-table${vendorGrouped
                ? " email-triage-dispatch-table--grouped"
                : ""
                }`}
            >
              {renderDispatchTableHeader()}

              <tbody>
                {renderDispatchTableBodyRows()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
  const skeletonTableColumns = Math.max(totalColumnsForGroupedTable, 10);
  const skeletonTableRows = Math.min(Math.max(itemsPerPage, 5), 10);
  function handleVendorGroupingToggle() {
    setVendorGrouped((prev) => !prev);
    setCurrentPage(1);
    setTableData([]);
  }

  function renderGroupingControls() {
    return (
      <>
        <div style={{ padding: "6px 0px" }}>
          <button
            type="button"
            className={`email-triage-group-toggle${vendorGrouped
              ? " email-triage-group-toggle--active"
              : ""
              }`}
            onClick={handleVendorGroupingToggle}
            aria-pressed={vendorGrouped}
            title={
              vendorGrouped
                ? "Show flat list"
                : "Group rows by vendor"
            }
          >
            <Mail size={14} aria-hidden />
            {vendorGrouped ? "Grouped" : "Group"}
          </button>
        </div>

        <div
          style={{
            paddingLeft: "20px",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <button
            type="button"
            className="reset-filter-button"
            onClick={resetFilter}
            title="Reset filters"
            aria-label="Reset filters"
          >
            <img
              src={resetBtn}
              alt=""
              aria-hidden="true"
            />
          </button>

          <OverflowMenu menuItems={overflowMenuItems} />
        </div>
      </>
    );
  }

  function renderGroupedVendorHint() {
    if (!vendorGrouped) {
      return null;
    }

    return (
      <div
        className="email-triage-grouped-hint-box"
        style={{ marginTop: "10px" }}
      >
        <div className="email-triage-grouped-hint">
          <Mail
            size={12}
            style={{ color: "#2563eb" }}
          />

          <span style={{ fontWeight: 600 }}>
            Viewing grouped entries by vendor
          </span>

          <span style={{ color: "#2563eb" }}>
            • Entries with same vendor are clubbed together
          </span>
        </div>
      </div>
    );
  }
  return (
    <>
      <Snackbar
        open={Boolean(deleteErrorSnackbar)}
        autoHideDuration={6000}
        onClose={() => setDeleteErrorSnackbar("")}
        message={deleteErrorSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <div className={styles.rmpmTableBody}>
        {initialPageLoading ? (
          <MonitorTodayPageSkeleton
            tableRows={skeletonTableRows}
            tableColumns={skeletonTableColumns}
            filterCount={5}
          />
        ) : (
          <>
            <div className="summary-wrapper">
              <div className="summary-grid">
                <div
                  className={`summary-card summary-card--kpi${kpiTotalActive ? " summary-card--kpi-active" : ""}`}
                >
                  <div className="summary-card-content">
                    {renderKpiTotalMetric(
                      "Total POs",
                      getTotalKpiCount(kpis),
                      getTotalKpiBreakdown(kpis),
                      {
                        mainConfig: {
                          isActive: kpiTotalActive,
                          onClick: () => handleKpiFilterClick("all"),
                        },
                        breakdownConfig: kpiTotalBreakdownConfig,
                      },
                    )}
                    <div className="summary-icon icon-blue">
                      <Mail size={18} />
                    </div>
                  </div>
                </div>

                <div
                  className={`summary-card summary-card--kpi${kpiPreponedActive ? " summary-card--kpi-active" : ""}`}
                >
                  <div className="summary-card-content">
                    {renderKpiMetricGroup(kpis, "POs Preponed", "Preponed", {
                      mainConfig: {
                        isActive: kpiPreponedMainActive,
                        onClick: () => handleKpiFilterClick("preponed"),
                      },
                      breakdownConfig: kpiCategoryBreakdownConfig("preponed"),
                    })}
                    <div className="summary-icon icon-amber">
                      <TrendingUp size={18} />
                    </div>
                  </div>
                </div>

                <div
                  className={`summary-card summary-card--kpi${kpiOnTimeActive ? " summary-card--kpi-active" : ""}`}
                >
                  <div className="summary-card-content">
                    {renderKpiMetricGroup(kpis, "POs On Time", "On-Time", {
                      mainConfig: {
                        isActive: kpiOnTimeMainActive,
                        onClick: () => handleKpiFilterClick("on_time"),
                      },
                      breakdownConfig: kpiCategoryBreakdownConfig("on_time"),
                    })}
                    <div className="summary-icon icon-emerald">
                      <CheckCircle2 size={18} />
                    </div>
                  </div>
                </div>

                <div
                  className={`summary-card summary-card--kpi${kpiDelayedActive ? " summary-card--kpi-active" : ""}`}
                >
                  <div className="summary-card-content">
                    {renderKpiMetricGroup(kpis, "POs Delayed", "Delayed", {
                      mainConfig: {
                        isActive: kpiDelayedMainActive,
                        onClick: () => handleKpiFilterClick("delayed"),
                      },
                      breakdownConfig: kpiCategoryBreakdownConfig("delayed"),
                    })}
                    <div className="summary-icon icon-red">
                      <AlertCircle size={18} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={styles.filters}
              style={{ width: "100%", display: "flex", alignItems: "end", flexWrap: "wrap" }}
            >
              {rmpmFilters
                .filter((filter) => filter.isFavourite)
                .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
                .map((filter) => {
                  const key = Object.keys(filterOptions).find((k) => generateLabel(k) === filter.label);
                  if (!key) return null;
                  const options = getFilterDropdownOptions(key);
                  const displayFilterValues = toDisplayEmailTriageFilterValues(
                    key,
                    selectedFilters[key] || [],
                  );

                  return (
                    <ListItem key={key} className={styles.listCustomClass}>
                      <MultiSelectWithSearchInput
                        label={filter.label}
                        options={options}
                        filterValues={displayFilterValues}
                        handleFilter={handleMultiSelectChange}
                        disabled={isScopeFilterLocked(key)}
                        type={alertType}
                      />
                    </ListItem>
                  );
                })}
              {renderGroupingControls()}

            </div>

            {renderGroupedVendorHint()}

            {renderDispatchTable()}

            {renderPagination()}

            <DraftEmail
              emailModalOpen={emailModalOpen}
              setEmailModalOpen={setEmailModalOpen}
              historyDraftEmails={historyDraftEmails}
              historyInboxEmails={historyInboxEmails}
              historySentEmails={historySentEmails}
              emailListTab={emailListTab}
              setEmailListTab={setEmailListTab}
              selectedPO={selectedPO}
              onRefreshEmailHistory={refreshEmailHistoryForModal}
              onRemoveLocalDraftEmail={removeLocalDraftEmail}
              onActionError={setDeleteErrorSnackbar}
              emailHistoryLoading={emailModalLoading}
              autoComposeIfNoDrafts={replyNowAutoCompose}
              isReplyNowCompose={isReplyNowCompose}
              onReplyNowAutoComposeComplete={handleReplyNowAutoComposeComplete}
              selectedDispatchRows={selectedDispatchRows}
              vendorGrouped={vendorGrouped}
            />

            <TripTrackingModal
              open={Boolean(tripIdModalOpen)}
              tripId={tripIds[0]}
              row={tripIds[0]}
              tripIds={tripIds}
              rowsByTripId={tripIds}
              alertType={alertType}
              onClose={() => setTripIdModalOpen(false)}
            />

            {filterDrawer && rmpmFilters.length > 0 && (
              <MoreFilters
                anchor="right"
                filterDrawer={filterDrawer}
                handleFilterDrawer={closeMoreFiltersDrawer}
                rmpmFilters={rmpmFilters}
                setRmpmFilters={setRmpmFilters}
                options={rmpmFiltersOption}
                onFiltersSaved={handleFiltersSaved}
              />
            )}

            {customiseColumnsOpen && (
              <CustomiseColumns
                anchor="right"
                columns={dispatchColumnList}
                defaultColumns={DISPATCH_COLUMNS.filter((c) => c.key === "srNo" || c.key === "_action").map((c) => c.label)}
                isOpen={customiseColumnsOpen}
                handleCustomizeDrawer={closeCustomiseColumnsDrawer}
                columnCustomization={handleDispatchColumnCustomization}
                username={activeAccount?.username ?? ""}
                type="Email Triage"
                persistToServer={false}
              />
            )}

            <Drawer anchor="right" open={openDrawer} onClose={toggleDrawer(false)} sx={{ '& .MuiDrawer-paper': { width: "95%" } }}>
              {alertDetailsForInsight ? (
                <AlertInsight
                  alertDetails={alertDetailsForInsight}
                  onCancel={toggleDrawer(false)}
                  setDrawerState={setOpenDrawer}
                  alertType="RMPM Alert"
                  isReadOnly={false}
                />
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <CircularProgress />
                </Box>
              )}
            </Drawer>
          </>
        )}
      </div>
    </>
  );
}

EmailTriagePage.propTypes = {
  alertType: PropTypes.string,
};

export default EmailTriagePage;
